import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const FEATURES = [
    "Corporate luncheons & office gatherings",
    "Weddings, rehearsal dinners & receptions",
    "Birthdays, anniversaries, celebrations",
    "Family reunions & community gatherings",
    "Buffet, family-style or plated service",
    "Vegan, vegetarian & dietary-friendly options",
];

const CATERING_IMG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/lldhlwvh_4D544B4D-5ED1-4118-B68A-2CABF55656D8%202.PNG";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Catering() {
    return (
        <section
            id="catering"
            data-testid="catering-section"
            className="relative empire-section bg-charcoal"
            aria-label="Catering"
        >
            <div className="empire-container">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    {/* Text */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-6 order-2 lg:order-1"
                    >
                        <p className="empire-eyebrow">Catering · Hudson Valley</p>
                        <h2
                            data-testid="catering-heading"
                            className="mt-5 empire-subhead text-cream"
                        >
                            Caribbean catering,<br />
                            <span className="italic text-gold">built around your night.</span>
                        </h2>
                        <p className="empire-body mt-6 max-w-xl">
                            From intimate dinners to corporate events of 300+, we bring
                            soulful Jamaican cooking to your space. Fully staffed,
                            beautifully presented, tuned to your guest count, dietary
                            needs, and the feeling you want in the room.
                        </p>

                        <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                            {FEATURES.map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-3 text-cream/80 text-[14px] font-light leading-relaxed"
                                >
                                    <span className="mt-1 inline-flex h-4 w-4 items-center justify-center border border-gold/60 shrink-0">
                                        <Check size={10} className="text-gold" />
                                    </span>
                                    {f}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-12 flex flex-col sm:flex-row gap-4">
                            <button
                                type="button"
                                onClick={() => scrollTo("catering-inquiry")}
                                data-testid="catering-cta-quote"
                                className="empire-btn-primary"
                            >
                                Request Catering Quote
                                <ArrowRight size={14} />
                            </button>
                            <a
                                href="tel:+18458490497"
                                data-testid="catering-cta-call"
                                className="empire-btn-secondary"
                            >
                                Call (845) 849-0497
                            </a>
                        </div>
                    </motion.div>

                    {/* Image */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-6 order-1 lg:order-2 relative"
                    >
                        <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden">
                            <img
                                src={CATERING_IMG}
                                alt="Empire Jamaica Fusion catering buffet — Jamaican entrées, sides, and fresh tropical punch set for a private event"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover"
                                style={{ filter: "contrast(1.06) saturate(1.05) brightness(0.97)" }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/35 via-transparent to-charcoal/10" />
                            <div className="absolute -inset-px border border-gold/20 pointer-events-none" />
                        </div>
                        {/* Floating spec card */}
                        <div className="hidden md:flex absolute -bottom-10 -left-10 bg-charcoal-800 border border-cream/10 px-7 py-6 flex-col gap-1 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
                            <span className="empire-eyebrow">Capacity</span>
                            <span className="font-serif text-3xl text-cream tracking-tight">
                                10 – 300+ guests
                            </span>
                            <span className="text-cream/55 text-[12px] font-light">
                                Delivery, buffet & full-service
                            </span>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
